import { apiClient } from './apiClient';
import { httpClient } from './httpClient';

export interface ReferenceFile {
  id: number;
  project_id: number;
  task_id: number | null;
  category: 'sample' | 'dedup' | 'other' | null;
  file_name: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by: number;
  uploaded_by_name: string | null;
  created_at: string;
}

const referenceFileService = {
  listForProject: async (projectId: number): Promise<ReferenceFile[]> => {
    const response = await apiClient.get<ReferenceFile[]>(`fdc/reference-files/by-project/${projectId}`);
    return response.data;
  },

  // The Sample/De-dup/Other files attached to one specific subtask - never
  // included in listForProject above (see the backend model's docstring),
  // visible only to that subtask's own members.
  listForTask: async (taskId: number): Promise<ReferenceFile[]> => {
    const response = await apiClient.get<ReferenceFile[]>(`fdc/reference-files/by-task/${taskId}`);
    return response.data;
  },

  // Multipart, so it goes through axios directly, since apiClient.post
  // always JSON.stringifies its body and can't carry a FormData/file payload.
  // taskId/category together attach this upload to one specific subtask
  // instead of the plain project-wide list - omit both for the original,
  // project-level behavior.
  upload: async (projectId: number, file: File, taskId?: number, category?: 'sample' | 'dedup' | 'other'): Promise<ReferenceFile> => {
    const formData = new FormData();
    formData.append('project_id', String(projectId));
    if (taskId != null) formData.append('task_id', String(taskId));
    if (category) formData.append('category', category);
    formData.append('file', file);
    const response = await httpClient.post<ReferenceFile>(
      'fdc/reference-files/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  download: async (fileId: number, fileName: string): Promise<void> => {
    const response = await httpClient.get<Blob>(
      `fdc/reference-files/${fileId}/download`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  delete: async (fileId: number): Promise<void> => {
    await apiClient.delete(`fdc/reference-files/${fileId}`);
  },
};

export default referenceFileService;
